import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Monitor, Smartphone, Shield, CheckCircle, AlertCircle } from 'lucide-react';

export function MobileLoginPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('form'); // form, success, error, expired
  const [sessionInfo, setSessionInfo] = useState(null);

  useEffect(() => {
    // Verifica che la sessione sia valida
    checkSessionValidity();
  }, [sessionId]);

  const checkSessionValidity = async () => {
    try {
      const response = await fetch(`/api/auth/qr/status/${sessionId}`);
      const session = await response.json();
      
      if (session.status === 'invalid') {
        setError('Sessione QR non valida o scaduta');
        setStatus('error');
      } else if (session.status === 'expired') {
        setError('Sessione QR scaduta. Genera un nuovo codice dal PC.');
        setStatus('expired');
      } else if (session.status === 'authenticated') {
        setStatus('success');
      } else {
        setSessionInfo(session);
      }
    } catch (error) {
      console.error('Errore verifica sessione:', error);
      setError('Errore di connessione');
      setStatus('error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!credentials.username || !credentials.password) {
      setError('Username e password sono richiesti');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`/api/auth/qr/authenticate/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });

      const result = await response.json();

      if (result.success) {
        setStatus('success');
        // Reset form per sicurezza
        setCredentials({ username: '', password: '' });
      } else {
        setError(result.error || 'Credenziali non valide');
      }
    } catch (error) {
      console.error('Errore autenticazione mobile:', error);
      setError('Errore di connessione');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const renderContent = () => {
    switch (status) {
      case 'success':
        return (
          <div className="text-center space-y-6">
            <div className="text-green-400">
              <CheckCircle size={64} className="mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Autenticazione Completata!</h2>
              <p className="text-gray-300">Il PC è stato autenticato con successo.</p>
            </div>
            
            <div className="bg-green-500/20 border border-green-400/50 rounded-lg p-4">
              <div className="flex items-center gap-3 text-green-300">
                <Monitor size={20} />
                <span className="font-medium">Accesso PC autorizzato</span>
              </div>
              <p className="text-sm text-green-200/80 mt-2">
                Puoi ora chiudere questa pagina e tornare al PC.
              </p>
            </div>

            <button
              onClick={() => window.close()}
              className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
            >
              Chiudi Pagina
            </button>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center space-y-6">
            <div className="text-amber-400">
              <AlertCircle size={64} className="mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Sessione Scaduta</h2>
              <p className="text-gray-300">Questo QR code è scaduto.</p>
            </div>
            
            <div className="bg-amber-500/20 border border-amber-400/50 rounded-lg p-4 text-amber-200">
              <p className="text-sm">
                Genera un nuovo QR code dal PC per continuare l'autenticazione.
              </p>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="text-center space-y-6">
            <div className="text-red-400">
              <AlertCircle size={64} className="mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Errore</h2>
              <p className="text-gray-300">{error}</p>
            </div>

            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
            >
              Riprova
            </button>
          </div>
        );

      default:
        return (
          <div className="space-y-6">
            {/* Header con indicazioni chiare */}
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-3 text-blue-400">
                <Smartphone size={32} />
                <span className="text-2xl">→</span>
                <Monitor size={32} />
              </div>
              
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  Accesso Remoto PC
                </h2>
                <p className="text-gray-300 text-sm">
                  Stai autenticando l'accesso per un computer remoto
                </p>
              </div>
            </div>

            {/* Alert di sicurezza */}
            <div className="bg-blue-500/20 border border-blue-400/50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Shield size={20} className="text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="text-blue-200 font-medium mb-1">
                    🔐 Autenticazione Sicura
                  </p>
                  <p className="text-blue-200/80">
                    Le tue credenziali verranno utilizzate per autorizzare 
                    l'accesso sul PC da cui hai scansionato il QR code.
                  </p>
                </div>
              </div>
            </div>

            {/* Form di login */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
                  Username
                </label>
                <input
                  id="username"
                  type="text"
                  value={credentials.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200"
                  placeholder="Il tuo username"
                  required
                  autoComplete="username"
                  disabled={isLoading}
                  autoFocus
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200"
                  placeholder="La tua password"
                  required
                  autoComplete="current-password"
                  disabled={isLoading}
                />
              </div>

              {error && (
                <div className="p-3 bg-red-500/20 border border-red-400/50 rounded-lg text-red-300 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold rounded-lg transition-all duration-200 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Autenticazione PC...
                  </div>
                ) : (
                  "🔐 Autorizza Accesso PC"
                )}
              </button>
            </form>

            {/* Timer se disponibile */}
            {sessionInfo?.expiresIn && (
              <div className="text-center text-xs text-gray-500">
                QR code valido ancora per: {Math.floor(sessionInfo.expiresIn / 60)}:{(sessionInfo.expiresIn % 60).toString().padStart(2, '0')}
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header con logo (solo se non success) */}
        {status !== 'success' && (
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-6">
              <img 
                src={typeof window !== 'undefined' && window.ktvEnv && window.ktvEnv.isElectron
                  ? new URL('../../public/kainolab_logo.png', import.meta.url).href
                  : '/kainolab_logo.png'}
                alt="KAINO-TV Logo" 
                className="w-12 h-12 object-contain rounded-lg"
              />
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                kainoTV
              </h1>
            </div>
          </div>
        )}

        {/* Content Container */}
        <div className="bg-gray-950/90 backdrop-blur-sm border border-gray-800/50 rounded-2xl p-8 shadow-2xl">
          {renderContent()}
        </div>

        {/* Footer info (solo se form) */}
        {status === 'form' && (
          <div className="text-center mt-6">
            <p className="text-gray-500 text-sm">
              Accesso sicuro tramite mobile • QR code valido 5 minuti
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default MobileLoginPage;
